// Import components
import CreateButton from "global/components/CreateButton";
import RadialProgress from "global/components/RadialProgress";
import ImagePopup from "global/components/ImagePopup";



const formatExpiresRemaining = (expiresValue) => {

  if (!expiresValue) return "No Expiry";

  const now = new Date();

  const expires = new Date(expiresValue);

  const diff = expires - now;

  if (diff <= 0) return "Expired";

  const seconds = Math.floor(diff / 1000);

  const minutes = Math.floor(diff / (1000 * 60));

  const hours = Math.floor(diff / (1000 * 60 * 60));

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days > 0) return `${days} day${days > 1 ? "s" : ""}`;

  if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""}`;

  if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""}`;

  return `${seconds} second${seconds !== 1 ? "s" : ""}`;

};



// Build the button title shown for a related bucket file.
const getBucketChildTitle = (child) => {

  if (child?.name) return child.name;

  return "Associated File";

};



const QueueItemPopup = ({
  focusImage,
  onClose,
  canNavigateFocusImage,
  onShowPrevImage,
  onShowNextImage,
  focusImageIsInProgress,
  focusImageIsQueued,
  focusImageImagePrompt,
  focusImageTextPrompt,
  focusImageOutputEntries,
  focusImageOutputDescription,
  isLikelyImageSource,
  focusImageProgressValue,
  focusImageProgressColor,
  focusImageWorkflow,
  focusImageEditableSettings,
  isEmptyFocusSettingValue,
  isWorkflowImageField,
  isWorkflowPromptField,
  formatFocusSettingValue,
  showSaveStatusForCurrentFocus,
  saveImageToBucketStatus,
  onCreatePodProduct,
  hasReusableTextPrompt,
  handleUseOutputDescription,
  handleUsePrompt,
  handleUseImage,
  handleReuseFocusImageSettings,
  imageInputWorkflows,
  handleRunFocusImageWorkflow,
  handleOpenCustomizrClick,
  // This tells us when the Customizr script has finished loading.
  isCustomizrReady,
  isSavingCurrentFocusImage,
  handleSaveImageToBucket,
  isSavingImageToBucket,
  handleDeleteImage,
}) => {

  const normalizedOutputEntries = Array.isArray(focusImageOutputEntries) ? focusImageOutputEntries : [];

  // Full queue-item responses can contain a primary bucket item plus related child files.
  let focusImageAssociatedFiles = [];

  if (focusImage?.bucket?.image_url) focusImageAssociatedFiles.push(focusImage.bucket);

  if (Array.isArray(focusImage?.bucket?.children)) {
    focusImageAssociatedFiles = [
      ...focusImageAssociatedFiles,
      ...focusImage.bucket.children
    ];
  }

  // Build the status text for saving the complete queue result to the user's bucket.
  let saveToBucketStatusMessage = "Saving to bucket...";

  // Show completion wording after the API successfully copies the queue result.
  if (saveImageToBucketStatus === "saved") { saveToBucketStatusMessage = "Saved to bucket."; }

  // Build the save button title without tying the action specifically to an image.
  let saveToBucketButtonTitle = "Save To Bucket";

  // Show progress wording while the save request is running.
  if (isSavingCurrentFocusImage) { saveToBucketButtonTitle = "Saving..."; }

  const hasOutputDescription = typeof focusImageOutputDescription === "string"

    && focusImageOutputDescription.trim().length > 0;

  const showOutputMediaContent = !focusImageIsInProgress
    && !focusImage?.image_url
    && normalizedOutputEntries.length > 0;

  const countdownValue = Number(focusImage?.countdown);

  const hasQueueCountdown = Number.isFinite(countdownValue) && countdownValue > 0;

  const inProgressMetricLabel = focusImageIsQueued && hasQueueCountdown ? "In Queue" : "Progress";

  const normalizedCountdownDisplay = Number.isInteger(countdownValue)
    ? String(countdownValue)
    : countdownValue.toFixed(1);

  const inProgressMetricValue = focusImageIsQueued && hasQueueCountdown
    ? normalizedCountdownDisplay
    : `${Math.max(0, Math.min(100, Math.round(focusImageProgressValue)))}%`;

  return (

    <ImagePopup
      focusImage={focusImage}
      alt={focusImage?.settings?.prompt || "Focus Image"}
      onClose={onClose}
      onLeft={canNavigateFocusImage ? onShowPrevImage : undefined}
      onRight={canNavigateFocusImage ? onShowNextImage : undefined}
      mediaContent={focusImageIsInProgress ? (

        // In-progress jobs show prompt/input context instead of a finished output image.
        <div className="ai-in-progress-popup-main">

          <h2 className="ai-in-progress-popup-main__title">Job submitted</h2>

          <div className="ai-in-progress-popup-main__content">

            {focusImageImagePrompt && isLikelyImageSource(focusImageImagePrompt) ? (

              <img
                src={focusImageImagePrompt}
                alt="Prompt input"
                className="ai-in-progress-popup-main__image"
              />

            ) : (

              <p className="ai-in-progress-popup-main__text">

                {focusImageTextPrompt || "Prompt not available for this job."}

              </p>

            )}

          </div>

        </div>

      ) : showOutputMediaContent ? (

        // Output-only jobs render their output values as the main popup content.
        <div className="ai-output-popup-main">

          <h2 className="ai-output-popup-main__title">Output</h2>

          <div className="ai-output-popup-main__content">

            <div className="ai-output-card ai-output-card--popup">

              <div className="ai-output-card-title">{focusImageTextPrompt || "Output details"}</div>

              <div className="ai-output-card-grid ai-output-card-grid--popup">

                {normalizedOutputEntries.map((entry, index) => (

                  <div key={`popup-output-${entry.key || index}-${index}`} className="ai-output-field">

                    <div className="ai-output-field-label">{entry.label}</div>

                    <div className="ai-output-field-value">{entry.value}</div>

                  </div>
                ))}

              </div>

            </div>

          </div>
          
        </div>

      ) : null}

    >
      {focusImage && (

        focusImageIsInProgress ? (

          // In-progress layout: progress ring + submission metadata.
          <div className="ai-image-focus-details ai-image-focus-details--in-progress">

            <div className="ai-focus-in-progress-progress">

              <div className="ai-image-progress-circle ai-image-progress-circle--popup">

                <RadialProgress
                  value={focusImageProgressValue}
                  direction={"clockwise"}
                  size={160}
                  progressColor={focusImageProgressColor}
                  bgColor={"var(--input-border, #b3b3b3)"}
                />

                <div className="ai-image-progress-text ai-image-progress-text--popup">

                  <div>{focusImage.status || "Queued"}</div>

                  <div>{focusImage.run_by || "Pending"}</div>

                </div>

              </div>

            </div>

            <div className="ai-focus-in-progress-meta">

              <div className="ai-focus-in-progress-meta-item">

                <h3 className="ai-image-focus-title">Workflow</h3>

                <p className="ai-image-focus-prompt">{focusImageWorkflow?.name || focusImage.workflow_code || focusImage.workflow_id || "Unknown"}</p>

              </div>

              <div className="ai-focus-in-progress-meta-item">

                <h3 className="ai-image-focus-title">{inProgressMetricLabel}</h3>

                <p className="ai-image-focus-prompt">{inProgressMetricValue}</p>

              </div>

              <div className="ai-focus-in-progress-meta-item">

                <h3 className="ai-image-focus-title">Submitted</h3>

                <p className="ai-image-focus-prompt">

                  {focusImage.created || focusImage.started
                    ? new Date(focusImage.created || focusImage.started).toLocaleString()
                    : "Unknown"}

                </p>

              </div>

              <div className="ai-focus-in-progress-meta-item">

                <h3 className="ai-image-focus-title">Job</h3>

                <p className="ai-image-focus-prompt">{focusImage.job || "Unknown"}</p>

              </div>

            </div>

          </div>

        ) : (

          // Completed layout: workflow/settings info and post-generation actions.
          <div className="ai-image-focus-details">

            <h3 className="ai-image-focus-title">Workflow</h3>

            <p className="ai-image-focus-prompt">{focusImageWorkflow?.name || focusImage.workflow_code || focusImage.workflow_id || "Unknown"}</p>

            {focusImageEditableSettings.filter((field) => !isEmptyFocusSettingValue(focusImage.settings?.[field.setting])).length > 0 ? (

              // Render only non-empty user-editable settings to keep details concise.
              <div className="ai-focus-settings-grid">

                {focusImageEditableSettings
                  .filter((field) => !isEmptyFocusSettingValue(focusImage.settings?.[field.setting]))
                  .map((field) => (

                    <div
                      key={`focus-setting-${field.setting}`}
                      className={`ai-focus-setting-item ${(isWorkflowImageField(field) || isWorkflowPromptField(field)) ? "ai-focus-setting-item--full" : ""}`}
                    >

                      <h3 className="ai-image-focus-title">{field.name || field.setting}</h3>

                      {isWorkflowImageField(field) && typeof focusImage.settings?.[field.setting] === "string" ? (

                        <img
                          src={focusImage.settings[field.setting]}
                          alt={field.name || field.setting}
                          className="ai-image-focus-setting-thumb"
                        />

                      ) : (

                        <p className="ai-image-focus-prompt">

                          {formatFocusSettingValue(field, focusImage.settings?.[field.setting])}

                        </p>

                      )}

                    </div>

                  ))}

              </div>

            ) : (

              <div>

                <h3 className="ai-image-focus-title">Settings</h3>

                <p className="ai-image-focus-prompt">No user-editable settings found for this workflow.</p>

              </div>
            )}

            {/* Base metadata for completed items. */}
            <div className="ai-image-focus-meta-grid">

              <div>

                <h3 className="ai-image-focus-title">Expires</h3>

                <p className="ai-image-focus-expires">

                  {formatExpiresRemaining(focusImage.expires)}

                </p>

              </div>

              <div>

                <h3 className="ai-image-focus-title">Created</h3>

                <p className="ai-image-focus-date">

                  {new Date(focusImage.created).toLocaleDateString()}

                </p>

              </div>

            </div>

            {/* Action stack: save status, POD creation, reuse, workflow runs, and image operations. */}
            <div className="ai-image-focus-buttons">

              {showSaveStatusForCurrentFocus && (

                <div className={`ai-focus-save-progress ai-focus-save-progress--${saveImageToBucketStatus}`} role="status" aria-live="polite">

                  <div className="ai-focus-save-progress__label">

                    {saveToBucketStatusMessage}

                  </div>

                  {saveImageToBucketStatus === "saving" && (

                    <div className="ai-focus-save-progress__track">

                      <div className="ai-focus-save-progress__bar" />

                    </div>

                  )}

                </div>

              )}

              {focusImage?.bucket && (

                // Save the complete queue bucket group, including associated child files.
                <CreateButton
                  title={saveToBucketButtonTitle}
                  onClick={() => {

                    handleSaveImageToBucket(focusImage);

                  }}
                  className="ai-image-btn ai-focus-primary-action"
                  disabled={isSavingImageToBucket}
                />

              )}

              {focusImage.image_url && (

                // Fast path to turn this output into a POD product.
                <CreateButton
                  title="Create POD product"
                  onClick={() => {

                    onCreatePodProduct({

                      // Pass the generated image into the normal POD design editor
                      image_url: focusImage.image_url,

                      // Use the same useful job title already shown in AI Studio
                      title: focusImageTextPrompt || "Untitled",

                    });

                    onClose();

                  }}
                  className="ai-image-btn ai-focus-primary-action"
                  color="#00a43b"
                />

              )}

              {focusImageAssociatedFiles.length > 0 && (

                // Show child bucket files returned by the full queue-item request.
                <div className="ai-focus-action-section">

                  <h3 className="ai-image-focus-title">Associated Files</h3>

                  <div className="ai-focus-action-grid">

                    {focusImageAssociatedFiles.map((child, index) => (

                      <CreateButton
                        key={`focus-bucket-child-${child?.bucket_id || child?.id || index}`}
                        title={getBucketChildTitle(child)}
                        onClick={() => {

                          if (!child?.image_url) return;

                          window.open(child.image_url, "_blank", "noopener,noreferrer");

                        }}
                        className="ai-image-btn"
                        disabled={!child?.image_url}
                      />

                    ))}

                  </div>

                </div>

              )}

              {/* Reuse content from this image back into workflow form inputs. */}
              <div className="ai-focus-action-section">

                <h3 className="ai-image-focus-title">Reuse</h3>

                <div className="ai-focus-action-grid">

                  {hasOutputDescription && (

                    <CreateButton
                      title="Description"
                      onClick={() => handleUseOutputDescription(focusImage)}
                      className="ai-image-btn"
                    />

                  )}

                  {hasReusableTextPrompt(focusImage?.settings) && (

                    <CreateButton
                      title="Text Prompt"
                      onClick={() => handleUsePrompt(focusImage.settings)}
                      className="ai-image-btn"
                    />

                  )}

                  {focusImage?.image_url && (

                    <CreateButton
                      title="Image"
                      onClick={() => handleUseImage(focusImage.image_url)}
                      className="ai-image-btn"
                    />

                  )}

                  <CreateButton
                    title="Settings"
                    onClick={handleReuseFocusImageSettings}
                    className="ai-image-btn"
                  />

                </div>

              </div>

              {focusImage.image_url && (

                /* Run image-capable workflows only for real image outputs. */
                <div className="ai-focus-action-section">

                  <h3 className="ai-image-focus-title">Image Workflows</h3>

                  {imageInputWorkflows.length === 0 ? (

                    <div className="ai-focus-workflow-empty">No image workflows available.</div>

                  ) : (

                    <div className="ai-focus-action-grid">

                      {imageInputWorkflows.map((workflow) => (

                        <CreateButton
                          key={`focus-workflow-${workflow.code}`}
                          title={workflow.name}
                          onClick={() => handleRunFocusImageWorkflow(workflow.code)}
                          className="ai-image-btn"
                        />

                      ))}

                    </div>

                  )}

                </div>

              )}

              {focusImage.image_url && (

                // Direct image operations for this focused output.
                <div className="ai-focus-action-section">

                  <h3 className="ai-image-focus-title">Image Actions</h3>

                  <div className="ai-focus-action-grid">

                    <CreateButton
                      title={
                        // Show loading text until Customizr is ready.
                        isCustomizrReady ? "Edit Image" : "Loading..."
                      }
                      onClick={() => { handleOpenCustomizrClick("edit", focusImage.image_url); }}
                      className="ai-image-btn ai-edit-btn"
                      disabled={
                        // Block clicks until Customizr is ready.
                        !isCustomizrReady
                      }
                    />

                    <CreateButton
                      title="Delete Image"
                      onClick={() => {

                        handleDeleteImage(focusImage.job);

                      }}
                      className="ai-image-btn ai-delete-btn"
                    />

                  </div>

                </div>

              )}

            </div>

          </div>
        )

      )}

    </ImagePopup>

  );

};

export default QueueItemPopup;



